"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import {
  applyResolvedColorMode,
  getClientColorModePreference,
  getClientSystemPrefersDark,
  getServerColorModePreference,
  getServerSystemPrefersDark,
  resolveColorMode,
  subscribeColorModePreference,
  subscribeSystemPrefersDark,
  writeStoredColorModePreference,
  type ColorModePreference,
  type ResolvedColorMode,
} from "@/lib/design-system/theme";

type ThemeContextValue = {
  preference: ColorModePreference;
  resolved: ResolvedColorMode;
  setPreference: (preference: ColorModePreference) => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

type ThemeProviderProps = {
  children: ReactNode;
  /** Cookie-backed preference from the root layout — keeps SSR/client radios in sync. */
  initialPreference?: ColorModePreference;
};

export function ThemeProvider({
  children,
  initialPreference = "system",
}: ThemeProviderProps) {
  const getServerSnapshot = useCallback(
    () => getServerColorModePreference(initialPreference),
    [initialPreference],
  );

  const preference = useSyncExternalStore(
    subscribeColorModePreference,
    getClientColorModePreference,
    getServerSnapshot,
  );
  const systemPrefersDark = useSyncExternalStore(
    subscribeSystemPrefersDark,
    getClientSystemPrefersDark,
    getServerSystemPrefersDark,
  );

  const resolved = useMemo(
    () => resolveColorMode(preference, systemPrefersDark),
    [preference, systemPrefersDark],
  );

  const setPreference = useCallback((next: ColorModePreference) => {
    writeStoredColorModePreference(next);
  }, []);

  useEffect(() => {
    applyResolvedColorMode(resolved);
  }, [resolved]);

  // Ensure cookie mirrors localStorage after mount (next SSR matches client).
  useEffect(() => {
    writeStoredColorModePreference(getClientColorModePreference());
  }, []);

  const value = useMemo(
    () => ({ preference, resolved, setPreference }),
    [preference, resolved, setPreference],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return context;
}
